/**
 * TRANSLATION KEYS, AND THE HASH THAT STOPS A STALE TRANSLATION FROM SHIPPING.
 * ===========================================================================
 *
 * ─── WHY KEYS ARE DERIVED, NOT DECLARED ────────────────────────────────────────────────────────
 *
 * The obvious design is to add `labelKey: 'install.visit_start.label'` next to every `label` in the
 * five schemas. It was rejected. 280 answerable fields means ~700 hand-written keys, every one of
 * which can be mistyped, duplicated, or — worst — attached to the wrong field, which produces a
 * question whose text belongs to a different question. That defect is invisible in English (the
 * fallback path never runs) and only appears in the field, in a language the reviewer cannot read.
 *
 * So a key is **computed from where the string already sits in the schema tree**:
 *
 *     schema.install.field.visit_start.label
 *     schema.install.section.visit.title
 *     schema.maintenance.field.blocker.option.parts-on-order
 *
 * Consequences, good and bad, stated plainly:
 *
 *   + Nothing in `src/forms/schemas/**` changes. No edit, no merge conflict with concurrent schema
 *     work, and no possibility of a key/label mismatch — the key *is* the label's address.
 *   + A schema stays serialisable. `FormSchema` gains no field; keys are derived on demand from the
 *     ids that are already there, so `JSON.parse(JSON.stringify(schema))` is still a valid schema.
 *   + Fallback is free and cannot render a raw key or a blank, because **the English string is still
 *     physically in the schema**. There is no separate English catalogue that could go missing. A
 *     missing translation resolves to the object literal the renderer would have read anyway.
 *   − Renaming a field id orphans its translations. This is the real cost. It is made loud rather
 *     than silent by the orphan test in `test/i18n/catalogue.test.ts`, which fails on any catalogue
 *     entry that extraction no longer produces. A rename becomes a red build, not a mystery.
 *
 * ─── WHY EVERY ENTRY CARRIES A HASH OF ITS ENGLISH SOURCE ───────────────────────────────────────
 *
 * The failure this project keeps finding is a wrong answer that looks like a right one. The i18n
 * version of that is a **stale translation**: someone reworks an English instruction — say
 * `"Isolate at the local disconnect"` becomes `"Isolate at the main breaker AND the local
 * disconnect"` — and the French catalogue, translated against the old wording, keeps resolving. The
 * app now renders confident, fluent, grammatical French that instructs a technician to do the wrong
 * thing to live electrical equipment. Nothing is missing, so no missing-translation check fires.
 *
 * So each catalogue entry records `sourceHash`, the hash of the English text it was translated
 * against, and a translation is used **only if that hash still matches the current English**. On a
 * mismatch the entry is treated as stale and the string falls back to English. English a technician
 * can puzzle out beats fluent instructions to do the wrong thing.
 *
 * The hash is FNV-1a/32, hand-rolled: it must be synchronous (`crypto.subtle` is async and cannot be
 * called from a render path), dependency-free (dependencies are the parent agent's to add), and
 * identical in Node and the browser. It is a **change detector, not a security primitive** — a
 * collision means one stale string survives, not a vulnerability, and 32 bits over ~700 strings is
 * ample for that job.
 */

import type { Field, FormSchema, Section } from '../forms/types.ts';

/** Which piece of a schema a string came from. Lets a translator see what they are translating. */
export type StringRole = 'schemaTitle' | 'sectionTitle' | 'sectionSubtitle' | 'label' | 'help' | 'placeholder' | 'patternMessage' | 'option';

export type TranslationKey = string;

/**
 * FNV-1a, 32-bit, rendered as 8 lowercase hex characters.
 *
 * `>>> 0` after the multiply keeps the value an unsigned 32-bit integer: `Math.imul` returns a
 * *signed* 32-bit result, and without the coercion `toString(16)` would emit a `-` for half of all
 * inputs, which is a valid-looking hash that no other implementation would reproduce.
 */
export function hashSource(text: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i += 1) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h.toString(16).padStart(8, '0');
}

/**
 * An option value reduced to a key-safe slug.
 *
 * Options are keyed by a slug of their **English value**, not by their index in the array. Index
 * keys break silently when someone reorders a picklist — every translation shifts by one and the
 * result is a grammatical, plausible, wrong answer list. A value-slug key instead breaks *loudly*
 * (orphan + missing) when an option's text changes, which is correct, because an option's text is
 * also its Salesforce picklist value: changing it is already a breaking change that deserves a
 * failing build.
 *
 * Non-alphanumerics collapse to a single `-`. Collisions are therefore possible in principle
 * (`"N/A"` and `"N A"` both slug to `n-a`) and are rejected outright by `extract.ts` rather than
 * silently merged, because two options sharing one key means one of them renders the other's text.
 */
export function optionSlug(value: string): string {
  const slug = value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  // An option consisting entirely of punctuation would otherwise produce an empty key segment,
  // which reads as "the field itself" and would collide with the label key.
  return slug.length > 0 ? slug : `x${hashSource(value)}`;
}

export function schemaTitleKey(schemaId: string): TranslationKey {
  return `schema.${schemaId}.title`;
}

export function sectionKey(schemaId: string, sectionId: string, role: 'title' | 'subtitle'): TranslationKey {
  return `schema.${schemaId}.section.${sectionId}.${role}`;
}

export function fieldKey(
  schemaId: string,
  fieldId: string,
  role: 'label' | 'help' | 'placeholder' | 'patternMessage',
): TranslationKey {
  return `schema.${schemaId}.field.${fieldId}.${role}`;
}

export function optionKey(schemaId: string, fieldId: string, optionValue: string): TranslationKey {
  return `schema.${schemaId}.field.${fieldId}.option.${optionSlug(optionValue)}`;
}

/* Re-exported so callers that only want key construction do not also have to import forms types. */
export type { Field, FormSchema, Section };
